import { useState } from "react";
import { X, Check, ArrowRight, ArrowLeft, FileText, Users, Send, Calendar, Sparkles } from "lucide-react";
import { useAlert } from "../../../../hooks/useAlert";

const STEPS = [
  { key: "details",  label: "Details",  icon: FileText },
  { key: "audience", label: "Audience", icon: Users },
  { key: "content",  label: "Content",  icon: Sparkles },
  { key: "schedule", label: "Schedule", icon: Calendar },
];

const TYPES = ["Newsletter", "Announcement", "Promotion", "Re-engagement", "Nurture"];
const SEGMENTS = [
  { id: "all", label: "All Subscribers", count: 45230 },
  { id: "active", label: "Active Clients", count: 28100 },
  { id: "engaged", label: "Engaged (30d)", count: 18750 },
  { id: "highintent", label: "High-Intent Leads", count: 8900 },
  { id: "dormant", label: "Dormant Leads", count: 12400 },
];
const TEMPLATES = [
  { id: "t1", name: "Clean Newsletter" },
  { id: "t2", name: "Product Announcement" },
  { id: "t3", name: "Promo / Offer" },
  { id: "t4", name: "Plain Text" },
];

function Field({ label, children, hint }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-text_secondary">{label}</span>
      <div className="mt-1.5">{children}</div>
      {hint && <span className="text-[11px] text-text_muted mt-1 block">{hint}</span>}
    </label>
  );
}

const inputCls =
  "w-full px-3.5 py-2.5 text-sm rounded-xl bg-surface border border-border text-text_primary placeholder:text-text_muted focus:outline-none focus:border-primary transition-colors";

export default function CampaignWizard({ open, onClose }) {
  const { showAlert } = useAlert();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    name: "", subject: "", type: "Newsletter",
    segment: "all", template: "t1",
    when: "now", scheduledAt: "",
  });

  if (!open) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const seg = SEGMENTS.find((s) => s.id === form.segment);

  const canNext = () => {
    if (step === 0) return form.name.trim() && form.subject.trim();
    return true;
  };

  const next = () => (step < STEPS.length - 1 ? setStep(step + 1) : finish());
  const back = () => step > 0 && setStep(step - 1);

  const finish = () => {
    showAlert({
      type: "success",
      message: form.when === "now"
        ? `Campaign "${form.name}" queued to send to ${seg?.count.toLocaleString()} contacts!`
        : `Campaign "${form.name}" scheduled successfully!`,
    });
    onClose();
    setStep(0);
    setForm({ name: "", subject: "", type: "Newsletter", segment: "all", template: "t1", when: "now", scheduledAt: "" });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(2px)" }} onClick={onClose} />

      <div
        className="relative w-full max-w-2xl max-h-[90vh] flex flex-col rounded-2xl border border-border bg-surface shadow-2xl overflow-hidden"
        style={{ boxShadow: "0 24px 60px rgba(0,0,0,0.35)" }}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-border flex-shrink-0">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center text-white flex-shrink-0"
            style={{ background: "linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%)" }}
          >
            <Send className="w-4 h-4" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-bold text-text_primary">New Campaign</h3>
            <p className="text-xs text-text_muted">Step {step + 1} of {STEPS.length} · {STEPS[step].label}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-text_muted hover:bg-surface_alt hover:text-text_primary transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Stepper */}
        <div className="flex items-center px-6 py-3 border-b border-border bg-surface_alt/40 flex-shrink-0">
          {STEPS.map((s, i) => {
            const done = i < step;
            const active = i === step;
            return (
              <div key={s.key} className="flex items-center flex-1 last:flex-none">
                <div className="flex items-center gap-2">
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors flex-shrink-0"
                    style={{
                      background: done || active ? "var(--color-primary)" : "var(--color-surface-alt)",
                      color: done || active ? "#fff" : "var(--color-text-muted)",
                      border: active ? "2px solid var(--color-secondary)" : "none",
                    }}
                  >
                    {done ? <Check className="w-3.5 h-3.5" /> : i + 1}
                  </div>
                  <span className="text-xs font-semibold hidden sm:block" style={{ color: active ? "var(--color-text-primary)" : "var(--color-text-muted)" }}>
                    {s.label}
                  </span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className="flex-1 h-px mx-2" style={{ background: done ? "var(--color-primary)" : "var(--color-border)" }} />
                )}
              </div>
            );
          })}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {step === 0 && (
            <div className="space-y-4">
              <Field label="Campaign name">
                <input className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Q3 Estimation Newsletter" autoFocus />
              </Field>
              <Field label="Email subject line" hint="Tip: keep under 60 characters for best open rates.">
                <input className={inputCls} value={form.subject} onChange={(e) => set("subject", e.target.value)} placeholder="e.g. Faster bids, fewer errors — see how" />
              </Field>
              <Field label="Campaign type">
                <div className="flex flex-wrap gap-2">
                  {TYPES.map((t) => (
                    <button
                      key={t}
                      onClick={() => set("type", t)}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors"
                      style={form.type === t
                        ? { background: "var(--color-primary-tint)", borderColor: "var(--color-primary)", color: "var(--color-primary)" }
                        : { background: "var(--color-surface)", borderColor: "var(--color-border)", color: "var(--color-text-muted)" }}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </Field>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-2.5">
              <p className="text-xs text-text_muted mb-1">Choose who receives this campaign.</p>
              {SEGMENTS.map((s) => (
                <button
                  key={s.id}
                  onClick={() => set("segment", s.id)}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-colors text-left"
                  style={form.segment === s.id
                    ? { background: "var(--color-primary-tint)", borderColor: "var(--color-primary)" }
                    : { background: "var(--color-surface)", borderColor: "var(--color-border)" }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "var(--color-surface-alt)" }}>
                      <Users className="w-4 h-4 text-text_muted" />
                    </div>
                    <span className="text-sm font-semibold text-text_primary">{s.label}</span>
                  </div>
                  <span className="text-xs font-bold text-text_secondary mono">{s.count.toLocaleString()}</span>
                </button>
              ))}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-2.5">
              <p className="text-xs text-text_muted mb-1">Pick a starting template — you can edit content later.</p>
              <div className="grid grid-cols-2 gap-3">
                {TEMPLATES.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => set("template", t.id)}
                    className="rounded-xl border p-3 text-left transition-colors"
                    style={form.template === t.id
                      ? { borderColor: "var(--color-primary)", background: "var(--color-primary-tint)" }
                      : { borderColor: "var(--color-border)", background: "var(--color-surface)" }}
                  >
                    <div className="h-20 rounded-lg mb-2 flex items-center justify-center" style={{ background: "var(--color-surface-alt)" }}>
                      <FileText className="w-6 h-6 text-text_muted" />
                    </div>
                    <span className="text-xs font-semibold text-text_primary">{t.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <Field label="When to send">
                <div className="grid grid-cols-2 gap-3">
                  {[{ k: "now", t: "Send now", d: "Queue immediately" }, { k: "later", t: "Schedule", d: "Pick date & time" }].map((o) => (
                    <button
                      key={o.k}
                      onClick={() => set("when", o.k)}
                      className="rounded-xl border p-3 text-left transition-colors"
                      style={form.when === o.k
                        ? { borderColor: "var(--color-primary)", background: "var(--color-primary-tint)" }
                        : { borderColor: "var(--color-border)", background: "var(--color-surface)" }}
                    >
                      <div className="text-sm font-semibold text-text_primary">{o.t}</div>
                      <div className="text-xs text-text_muted mt-0.5">{o.d}</div>
                    </button>
                  ))}
                </div>
              </Field>
              {form.when === "later" && (
                <Field label="Send date & time">
                  <input type="datetime-local" className={inputCls} value={form.scheduledAt} onChange={(e) => set("scheduledAt", e.target.value)} />
                </Field>
              )}

              {/* Review summary */}
              <div className="rounded-xl border border-border bg-surface_alt/40 p-4 space-y-2">
                <h4 className="text-xs font-bold text-text_primary uppercase tracking-wide mb-1">Review</h4>
                {[
                  ["Name", form.name || "—"],
                  ["Subject", form.subject || "—"],
                  ["Type", form.type],
                  ["Audience", `${seg?.label} (${seg?.count.toLocaleString()})`],
                  ["Template", TEMPLATES.find((t) => t.id === form.template)?.name],
                ].map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between text-xs">
                    <span className="text-text_muted">{k}</span>
                    <span className="text-text_primary font-medium text-right ml-3 truncate max-w-[60%]">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-border flex-shrink-0">
          <button
            onClick={back}
            disabled={step === 0}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium border border-border text-text_secondary hover:bg-surface_alt disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <button
            onClick={next}
            disabled={!canNext()}
            className="inline-flex items-center gap-1.5 px-5 py-2 rounded-xl text-sm font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
            style={{ background: "linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%)" }}
          >
            {step === STEPS.length - 1
              ? (<>{form.when === "now" ? "Send campaign" : "Schedule"} <Check className="w-4 h-4" /></>)
              : (<>Continue <ArrowRight className="w-4 h-4" /></>)}
          </button>
        </div>
      </div>
    </div>
  );
}
